import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { BrowserCacheLocation, Configuration, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../../environments/environment';
import * as amplitude from '@amplitude/unified';
import { ConsoleHelper } from '../core/static-classes/console-helper';

interface ProtectedResorce {
    endpoint: string;
    scopes: Array<string>;
}

@Injectable({
    providedIn: 'root'
})
export class AuthConfigService {
    private readonly httpClient: HttpClient;
    private readonly isIE = globalThis.window.navigator.userAgent.includes('MSIE ') || globalThis.window.navigator.userAgent.includes('Trident/');

    constructor(private readonly httpBackend: HttpBackend) {
        this.httpClient = new HttpClient(this.httpBackend);
    }

    /**
     * Configuration object to be passed to MSAL instance on creation.
     * For a full list of MSAL.js configuration parameters, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
     */
    public readonly msalConfig: Configuration = {
        auth: {
            clientId: '',
            authority: '',
            redirectUri: '',
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: this.isIE,
        },
        system: {
            loggerOptions: {
                loggerCallback(logLevel: LogLevel, message: string) {
                    ConsoleHelper.log(logLevel, message);
                },
                logLevel: LogLevel.Error,
                piiLoggingEnabled: false,
            },
        },
    };

    /**
     * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
     */
    public readonly protectedResources: Array<ProtectedResorce> = [
        { endpoint: `${document.location.origin}/api/*`, scopes: [] },
        // { endpoint: 'https://graph.microsoft.com/v1.0/me', scopes: ['User.Read'] },
    ];

    /**
     * Scopes you add here will be prompted for user consent during sign-in.
     * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
     * For more information about OIDC scopes, visit:
     * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
     */
    public readonly loginRequest = {
        scopes: [],
    };

    public loadEnvironment(): Promise<void> {
        const cacheBuster = Date.now();
        return new Promise<void>((resolve) => {
            this.httpClient.get<any>(`assets/environment.json?d=${cacheBuster}`).subscribe({
                next: (env) => {
                    this.msalConfig.auth.clientId = env.auth.clientId;
                    this.msalConfig.auth.authority = env.auth.authority;
                    this.msalConfig.auth.redirectUri = `${globalThis.location.origin}/auth`;
                    this.protectedResources[0].endpoint = env.apiUri;
                    this.protectedResources[0].scopes = [env.auth.scope];
                    environment.apiUri = env.apiUri;
                    environment.current = env.environment;
                    environment.testRunner = !!(globalThis.window as any)?.__karma__;

                    this.initAmplitude(env.amplitudeApiKey);

                    resolve();
                },
                error: (error) => {
                    console.error(`Error loading config file: ${error.message}`);
                    resolve();
                }
            });
        });
    }

    private initAmplitude(key: string): void {
        if (environment.testRunner) {
            return;
        }
        if (environment.current.toLowerCase() !== 'production') {
            return;
        }
        amplitude.initAll(key, {
            serverZone: 'EU',
            analytics: {
                defaultTracking: true,
            }
        });
        ConsoleHelper.log('Amplitude initialized');
    }

}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(authConfigService: AuthConfigService): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();

    authConfigService.protectedResources.forEach((el: ProtectedResorce) => {
        protectedResourceMap.set(el.endpoint, el.scopes);
    });

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: this.loginRequest,
    };
}

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(authConfigService: AuthConfigService): IPublicClientApplication {
    const config = authConfigService.msalConfig;
    return new PublicClientApplication(config);
}
